import React, { useState } from 'react';
import { ShieldCheckIcon, AlertCircleIcon, DollarSignIcon, SettingsIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface PreAuthPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
}
export function PreAuthPayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange
}: PreAuthPaymentProps) {
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [captureOption, setCaptureOption] = useState<'manual' | 'auto'>('manual');
  const [autoDays, setAutoDays] = useState('7');
  const handleAmountChange = (v: string) => onAmountChange?.(v);
  const handleReferenceChange = (v: string) => onReferenceChange?.(v);
  const handleExpiryDateChange = (v: string) => {
    setExpiryDate(v);
    onExpiryDateChange?.(v);
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
  return <div className="space-y-5" data-id="element-3344">
      {/* ─── PRE-AUTHORIZATION DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-3345">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-3346">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-3347">
            <ShieldCheckIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-3348" />
            Pre-authorization details
          </h2>
          <p className="text-xs text-gray-500 mt-1" data-id="element-3349">
            Place a temporary hold on the customer's card without charging them.
          </p>
        </div>

        <div className="p-6 space-y-4" data-id="element-3350">
          <div data-id="element-3351">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3352">
              Hold amount <span className="text-red-400" data-id="element-3353">*</span>
            </label>
            <div className="relative" data-id="element-3354">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3355">
                <span className="text-sm font-medium text-gray-500" data-id="element-3356">THB</span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" onChange={e => handleAmountChange(e.target.value)} data-id="element-3357" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3358">
            <div data-id="element-3359">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3360">
                Reference
              </label>
              <input type="text" className={inputClass} placeholder="e.g., Booking #1234" onChange={e => handleReferenceChange(e.target.value)} data-id="element-3361" />
            </div>
            <div data-id="element-3362">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3363">
                Description
              </label>
              <input type="text" className={inputClass} placeholder="e.g., Security deposit" data-id="element-3364" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── CAPTURE OPTIONS ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-3365">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-3366">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-3367">
            <SettingsIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-3368" />
            Capture options
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-3369">
            Choose how and when to charge the held funds.
          </p>
        </div>

        <div className={`flex items-center px-6 py-4 cursor-pointer transition-colors border-b border-gray-200 ${captureOption === 'manual' ? 'bg-[#F7941D]/5' : 'hover:bg-gray-50'}`} onClick={() => setCaptureOption('manual')} data-id="element-3370">
          <input type="radio" name="captureOption" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D]/20 border-gray-300 flex-shrink-0" checked={captureOption === 'manual'} onChange={() => setCaptureOption('manual')} data-id="element-3371" />
          <div className="ml-4" data-id="element-3372">
            <p className="text-sm font-medium text-gray-900" data-id="element-3373">Manual capture</p>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-3374">
              Charge funds manually when needed from the transaction detail
              page.
            </p>
          </div>
        </div>

        <div className={`flex items-start px-6 py-4 cursor-pointer transition-colors ${captureOption === 'auto' ? 'bg-[#F7941D]/5' : 'hover:bg-gray-50'}`} onClick={() => setCaptureOption('auto')} data-id="element-3375">
          <input type="radio" name="captureOption" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D]/20 border-gray-300 flex-shrink-0 mt-0.5" checked={captureOption === 'auto'} onChange={() => setCaptureOption('auto')} data-id="element-3376" />
          <div className="ml-4 flex-1" data-id="element-3377">
            <p className="text-sm font-medium text-gray-900" data-id="element-3378">
              Automatic capture
            </p>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-3379">
              Automatically charge the held amount after a set number of days.
            </p>
            {captureOption === 'auto' && <div className="mt-3 flex items-center gap-2" data-id="element-3380">
                <label className="text-xs text-gray-500" data-id="element-3381">Capture after</label>
                <input type="number" className="w-16 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-center focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" value={autoDays} onChange={e => setAutoDays(e.target.value)} min="1" max="30" onClick={e => e.stopPropagation()} data-id="element-3382" />
                <span className="text-xs text-gray-500" data-id="element-3383">days</span>
              </div>}
          </div>
        </div>
      </div>

      {/* ─── ADDITIONAL OPTIONS ─── */}
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} data-id="element-3384" />
    </div>;
}